import { useQuery } from "@tanstack/react-query";
import { Navigate, RouteObject, useParams } from "react-router";

import { RanaLoading, SystemStatusPage, TenantProvider } from "@nens/ranalib";
import { SentryErrorBoundary } from "@nens/sentry";

import { BasePage } from "@/components/base-page";
import { PUBLICATION_SEARCH_KEY, PublicationPublic } from "@/features/publications";
import { useRanaSearchState } from "@/hooks/router/rana-search-state";
import { ProcessProvider } from "@/lib/process-provider";
import { getPublicationQuery } from "@/queries/query-publication";
import { PublicTenantProvider, usePublicationShareKey } from "@/stores/tenant-provider-public";

import { InvitationAcceptPage } from "../../lib/auth/components/invitation-accept-page";
import { AdminPage } from "../pages/admin-page";
import { AdminTenantPage } from "../pages/admin-tenant-page";
import { TenantCataloguePage } from "../pages/tenant-catalogue-page";
import { TenantDashboardPage } from "../pages/tenant-dashboard-page";
import { TenantNewProjectPage } from "../pages/tenant-new-project-page";
import { TenantProjectsPage } from "../pages/tenant-projects-page";
import { TenantSettingsPage } from "../pages/tenant-settings-page";

import { LOGGED_OUT_ROUTE, NotLoggedInRoutes } from "./not-logged-in-routes";
import { ProjectRoutes } from "./project-routes";

const TenantRedirect = () => {
  const { tenant } = useParams();
  return <Navigate to={`/${tenant}`} replace />;
};

function PublicationPublicPage() {
  const [publicationId] = useRanaSearchState<string | undefined>(
    PUBLICATION_SEARCH_KEY,
    undefined,
    ["string", "undefined"]
  );
  const { isLoading, data } = useQuery(getPublicationQuery(publicationId));

  if (isLoading) return <RanaLoading isCentered />;
  if (!data) return <SystemStatusPage variant="no-publication-found" />;

  return (
    <BasePage pageSubTitle={undefined}>
      <PublicationPublic />
    </BasePage>
  );
}

export const tenantRouteObjects: RouteObject[] = [
  {
    path: "",
    element: (
      <SentryErrorBoundary appName="rana" FallbackComponent={SystemStatusPage}>
        <TenantDashboardPage />
      </SentryErrorBoundary>
    ),
  },

  {
    path: "settings",
    element: (
      <SentryErrorBoundary appName="rana" FallbackComponent={SystemStatusPage}>
        <TenantSettingsPage />
      </SentryErrorBoundary>
    ),
  },
  {
    path: "catalogue",
    element: (
      <SentryErrorBoundary appName="rana" FallbackComponent={SystemStatusPage}>
        <TenantCataloguePage />
      </SentryErrorBoundary>
    ),
  },
  {
    path: "projects",
    element: (
      <SentryErrorBoundary appName="rana" FallbackComponent={SystemStatusPage}>
        <TenantProjectsPage />
      </SentryErrorBoundary>
    ),
  },
  {
    path: "projects-new",
    element: (
      <SentryErrorBoundary appName="rana" FallbackComponent={SystemStatusPage}>
        <TenantNewProjectPage />
      </SentryErrorBoundary>
    ),
  },
  {
    path: "projects/:slug/*",
    element: (
      <SentryErrorBoundary appName="rana" FallbackComponent={SystemStatusPage}>
        <ProjectRoutes />
      </SentryErrorBoundary>
    ),
  },
  {
    path: "admin",
    element: (
      <SentryErrorBoundary appName="rana" FallbackComponent={SystemStatusPage}>
        <AdminPage />
      </SentryErrorBoundary>
    ),
  },
  {
    path: "admin/detail",
    element: (
      <SentryErrorBoundary appName="rana" FallbackComponent={SystemStatusPage}>
        <AdminTenantPage />
      </SentryErrorBoundary>
    ),
  },
  {
    path: "invitations/:invitationId/accept",
    element: (
      <SentryErrorBoundary appName="rana" FallbackComponent={SystemStatusPage}>
        <InvitationAcceptPage />
      </SentryErrorBoundary>
    ),
  },
  {
    path: LOGGED_OUT_ROUTE,
    element: <TenantRedirect />,
  },
  {
    path: "*",
    element: <SystemStatusPage variant="not-found" />,
  },
];

export function TenantRoutes({ children }: { children: React.ReactNode }) {
  const { tenant } = useParams();
  const publicationShareKey = usePublicationShareKey();

  if (!tenant) return null;

  if (publicationShareKey)
    return (
      <SentryErrorBoundary appName="rana" FallbackComponent={SystemStatusPage}>
        <PublicTenantProvider {...{ publicationShareKey, tenant }}>
          <PublicationPublicPage />
        </PublicTenantProvider>
      </SentryErrorBoundary>
    );

  return (
    <TenantProvider
      notLoggedInRoutes={(setIdentityProvider: (identityProvider: string) => void) => (
        <NotLoggedInRoutes tenant={tenant} setIdentityProvider={setIdentityProvider} />
      )}
      tenant={tenant}
    >
      <ProcessProvider>{children}</ProcessProvider>
    </TenantProvider>
  );
}
